import type { NameMotif } from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix
import type { CustomValue } from '@creanizer/utils'
import { Checkbox, Label } from '@creanizer/ui'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'

interface NameGeneratorControlsProps {
  rarity: number
  setRarity: (value: number) => void
  count: 10 | 50 | 100
  setCount: (value: 10 | 50 | 100) => void
  motif: NameMotif | ''
  setMotif: (value: NameMotif | '') => void
  customMotif: string
  setCustomMotif: (value: string) => void
  seed: string
  setSeed: (value: string) => void
  autoSeed: boolean
  setAutoSeed: (value: boolean) => void
  usePro: boolean
  setUsePro: (value: boolean) => void
  useProLite: boolean
  setUseProLite: (value: boolean) => void
  useGemini3FlashMode: boolean
  setUseGemini3FlashMode: (value: boolean) => void
  savedMotifs: CustomValue[]
  addCustomValue: (type: CustomValue['type'], value: string, label: string, generatorType?: CustomValue['generatorType'], nameType?: string) => Promise<void>
  useCustomValue: (id: string) => Promise<void>
  
  // 新しい文字列制約機能
  includeString?: string
  setIncludeString: (value: string) => void
  includePosition?: 'start' | 'end' | 'random' | 'given_name_start'
  setIncludePosition: (value: 'start' | 'end' | 'random' | 'given_name_start' | undefined) => void
  fixedFamilyName?: string
  setFixedFamilyName: (value: string) => void
  fixedGivenName?: string
  setFixedGivenName: (value: string) => void
  avoidString?: string
  setAvoidString: (value: string) => void
  minLength?: number
  setMinLength: (value: number | undefined) => void
  maxLength?: number
  setMaxLength: (value: number | undefined) => void
  rhymePattern?: string
  setRhymePattern: (value: string) => void
  givenNameKanaType?: 'katakana' | 'hiragana'
  setGivenNameKanaType: (value: 'katakana' | 'hiragana' | undefined) => void
}

export function NameGeneratorControls({
  rarity,
  setRarity,
  count,
  setCount,
  motif,
  setMotif,
  customMotif,
  setCustomMotif,
  seed,
  setSeed,
  autoSeed,
  setAutoSeed,
  usePro,
  setUsePro,
  useProLite,
  setUseProLite,
  useGemini3FlashMode,
  setUseGemini3FlashMode,
  savedMotifs,
  addCustomValue,
  useCustomValue,
  includeString,
  setIncludeString,
  includePosition,
  setIncludePosition,
  fixedFamilyName,
  setFixedFamilyName,
  fixedGivenName,
  setFixedGivenName,
  avoidString,
  setAvoidString,
  minLength,
  setMinLength,
  maxLength,
  setMaxLength,
  rhymePattern,
  setRhymePattern,
  givenNameKanaType,
  setGivenNameKanaType,
}: NameGeneratorControlsProps) {
  return (
    <div className="space-y-4 mb-6">
      <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
        詳細設定
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            珍しさレベル
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="10"
              value={rarity}
              onChange={(e) => setRarity(Number(e.target.value))}
              className="flex-1"
            />
            <span className="text-lg font-semibold w-8 text-center">{rarity}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            1-3: 一般的 / 4-6: やや独特 / 7-8: 個性的 / 9-10: 非常に珍しい
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              生成数
            </label>
            <select
              className="input"
              value={count}
              onChange={(e) => setCount(Number(e.target.value) as 10 | 50 | 100)}
            >
              <option value="10">10個</option>
              <option value="50">50個</option>
              <option value="100">100個</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              モチーフ（オプション）
            </label>
            <select
              className="input"
              value={motif}
              onChange={(e) => setMotif(e.target.value as NameMotif | '')}
            >
              <option value="">指定なし</option>
              <option value="flower">花・植物</option>
              <option value="gem">宝石・鉱物</option>
              <option value="star">星・天体</option>
              <option value="element">元素・自然現象</option>
              <option value="animal">動物・生物</option>
              <option value="nature">自然・風景</option>
              <option value="emotion">感情・心理</option>
              <option value="color">色彩・光</option>
              <option value="custom">カスタム...</option>
            </select>
            {motif === 'custom' && (
              <div className="mt-2 space-y-2">
                <input
                  type="text"
                  className="input"
                  placeholder="カスタムモチーフを入力（例: 機械、時間、音楽）"
                  value={customMotif}
                  onChange={(e) => setCustomMotif(e.target.value)}
                />
                {customMotif && (
                  <button
                    type="button"
                    onClick={async () => {
                      await addCustomValue('motif', customMotif, customMotif, 'name')
                      alert(`カスタムモチーフ "${customMotif}" を保存しました`)
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    💾 保存
                  </button>
                )}
              </div>
            )}
            {/* 保存済みカスタムモチーフ */}
            {savedMotifs.length > 0 && (
              <div className="mt-2">
                <label className="text-xs text-gray-600">保存済みカスタム:</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {savedMotifs.map(cv => (
                    <button
                      key={cv.id}
                      type="button"
                      onClick={() => {
                        setMotif('custom')
                        setCustomMotif(cv.value)
                        useCustomValue(cv.id)
                      }}
                      className="px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 rounded"
                    >
                      {cv.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              シード値
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoSeed}
                  onChange={(e) => setAutoSeed(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">自動生成</span>
              </label>
              <input
                type="number"
                className="input"
                placeholder="例: 12345"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                disabled={autoSeed}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="pro-mode"
              checked={usePro}
              onCheckedChange={(checked) => {
                setUsePro(checked as boolean)
                if (checked) setUseProLite(false) // Proモードがオンの時はProライトをオフ
              }}
            />
            <Label htmlFor="pro-mode" className="cursor-pointer">
              Proモード（高精度モデルを使用）
            </Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="pro-lite-mode"
              checked={useProLite}
              onCheckedChange={(checked) => {
                setUseProLite(checked as boolean)
                if (checked) setUsePro(false) // Proライトがオンの時はProモードをオフ
              }}
            />
            <Label htmlFor="pro-lite-mode" className="cursor-pointer flex items-center space-x-2">
              <span>Proモードライト（実験的）</span>
              <span className="text-xs text-muted-foreground">[thinking_budget: 1024]</span>
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="gemini3-flash-mode"
              checked={useGemini3FlashMode}
              onCheckedChange={(checked) => {
                setUseGemini3FlashMode(checked as boolean)
              }}
            />
            <Label htmlFor="gemini3-flash-mode" className="cursor-pointer flex items-center space-x-2">
              <span>Gemini 3 Flash モード（試験版）</span>
            </Label>
          </div>
        </div>

        {/* 文字列制約機能（アコーディオン） */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="constraints">
            <AccordionTrigger>文字列制約・高度な設定</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                {/* 必ず含める文字列 */}
                <div>
                  <Label htmlFor="include-string" className="block text-sm font-medium mb-2">
                    必ず含める文字列
                  </Label>
                  <div className="grid md:grid-cols-2 gap-2">
                    <Input
                      id="include-string"
                      type="text"
                      placeholder="例: アル、美、龍"
                      value={includeString || ''}
                      onChange={(e) => setIncludeString(e.target.value)}
                    />
                    <Select
                      value={includePosition || 'random'}
                      onValueChange={(value) => setIncludePosition(value as 'start' | 'end' | 'random' | 'given_name_start')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="start">先頭に含める</SelectItem>
                        <SelectItem value="end">末尾に含める</SelectItem>
                        <SelectItem value="random">ランダムな位置</SelectItem>
                        <SelectItem value="given_name_start">名前の先頭（フルネームのみ）</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    生成される名前に必ず含まれる文字・文字列を指定
                  </p>
                </div>

                {/* 固定名字・名前 */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fixed-family" className="block text-sm font-medium mb-2">
                      固定名字/ラストネーム
                    </Label>
                    <Input
                      id="fixed-family"
                      type="text"
                      placeholder="例: 田中、Smith、Garcia"
                      value={fixedFamilyName || ''}
                      onChange={(e) => setFixedFamilyName(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      日本人名の姓、海外名のラストネームを固定
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="fixed-given" className="block text-sm font-medium mb-2">
                      固定名前/ファーストネーム
                    </Label>
                    <Input
                      id="fixed-given"
                      type="text"
                      placeholder="例: 太郎、John、Maria"
                      value={fixedGivenName || ''}
                      onChange={(e) => setFixedGivenName(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      日本人名の名、海外名のファーストネームを固定
                    </p>
                  </div>
                </div>

                {/* 日本人名のカナ統一 */}
                <div>
                  <Label className="block text-sm font-medium mb-2">
                    日本人名の名前部分カナ統一（フルネームのみ）
                  </Label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="kanaType"
                        value=""
                        checked={!givenNameKanaType}
                        onChange={() => setGivenNameKanaType(undefined)}
                        className="mr-2"
                      />
                      <span className="text-sm">指定なし</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="kanaType"
                        value="katakana"
                        checked={givenNameKanaType === 'katakana'}
                        onChange={() => setGivenNameKanaType('katakana')}
                        className="mr-2"
                      />
                      <span className="text-sm">カタカナ統一</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="kanaType"
                        value="hiragana"
                        checked={givenNameKanaType === 'hiragana'}
                        onChange={() => setGivenNameKanaType('hiragana')}
                        className="mr-2"
                      />
                      <span className="text-sm">ひらがな統一</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    例: カタカナ→田中タロウ、ひらがな→佐藤はなこ
                  </p>
                </div>

                {/* 禁止文字列 */}
                <div>
                  <Label htmlFor="avoid-string" className="block text-sm font-medium mb-2">
                    禁止文字列
                  </Label>
                  <Input
                    id="avoid-string"
                    type="text"
                    placeholder="例: 死、殺、悪（カンマ区切り）"
                    value={avoidString || ''}
                    onChange={(e) => setAvoidString(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    名前に含めたくない文字・文字列を指定
                  </p>
                </div>

                {/* 文字数制限 */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min-length" className="block text-sm font-medium mb-2">
                      最小文字数
                    </Label>
                    <Input
                      id="min-length"
                      type="number"
                      placeholder="例: 2"
                      value={minLength || ''}
                      onChange={(e) => setMinLength(e.target.value ? Number(e.target.value) : undefined)}
                      min={1}
                      max={50}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max-length" className="block text-sm font-medium mb-2">
                      最大文字数
                    </Label>
                    <Input
                      id="max-length"
                      type="number"
                      placeholder="例: 10"
                      value={maxLength || ''}
                      onChange={(e) => setMaxLength(e.target.value ? Number(e.target.value) : undefined)}
                      min={1}
                      max={50}
                    />
                  </div>
                </div>

                {/* 韻パターン */}
                <div>
                  <Label htmlFor="rhyme-pattern" className="block text-sm font-medium mb-2">
                    韻パターン（語尾統一）
                  </Label>
                  <Input
                    id="rhyme-pattern"
                    type="text"
                    placeholder="例: ～ス、～ル、～ン"
                    value={rhymePattern || ''}
                    onChange={(e) => setRhymePattern(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    名前の語尾を統一したい場合に指定（韻を踏む名前）
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  )
}